import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Building2,
  Plus,
  Search,
  Pencil,
  Trash2,
  Users,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Eye,
  EyeOff,
  Save,
  X,
  Calendar,
  Hash,
  Activity,
  BarChart3,
  FolderOpen,
  MessageSquare,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { tenantsApi, Tenant, TenantUser, CreateTenantInput, UpdateTenantInput } from '@/services/api/tenants';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserCircle, Shield, KeyRound, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface FormData extends CreateTenantInput {
  adminEmail: string;
  adminPassword: string;
  adminFirstName: string;
  adminLastName: string;
  sendWelcomeEmail: boolean;
}

const initialFormData: FormData = {
  name: '',
  slug: '',
  plan: 'starter',
  status: 'active',
  adminEmail: '',
  adminPassword: '',
  adminFirstName: '',
  adminLastName: '',
  sendWelcomeEmail: true,
};

// Component for displaying tenant users
function TenantUsersTab({ tenantId }: { tenantId: string }) {
  const [userSearch, setUserSearch] = useState('');
  const [resetPasswordUser, setResetPasswordUser] = useState<TenantUser | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['tenant-users', tenantId, userSearch],
    queryFn: () => tenantsApi.getUsers(tenantId, {
      search: userSearch || undefined,
      limit: 50
    }),
    enabled: !!tenantId,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, email }: { userId: string; email: string }) =>
      tenantsApi.resetUserPassword(tenantId, userId).then(result => ({ ...result, email })),
    onSuccess: (data) => {
      toast.success('Password reset email sent', {
        description: `A temporary password has been sent to ${data.email}`,
      });
      setResetPasswordUser(null);
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to reset password';
      toast.error('Failed to reset password', { description: message });
      setResetPasswordUser(null);
    },
  });

  const users = data?.data || [];

  const getRoleBadge = (role: TenantUser['role']) => {
    switch (role) {
      case 'super_admin':
        return <Badge variant="destructive">Super Admin</Badge>;
      case 'tenant_admin':
        return <Badge className="bg-purple-500">Admin</Badge>;
      case 'manager':
        return <Badge variant="secondary">Manager</Badge>;
      default:
        return <Badge variant="outline">User</Badge>;
    }
  };

  const getStatusBadge = (status: TenantUser['status']) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500">Active</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inactive</Badge>;
      case 'suspended':
        return <Badge variant="destructive">Suspended</Badge>;
      case 'pending':
        return <Badge variant="outline">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleResetPassword = (user: TenantUser) => {
    setResetPasswordUser(user);
  };

  const confirmResetPassword = () => {
    if (resetPasswordUser) {
      resetPasswordMutation.mutate({ userId: resetPasswordUser.id, email: resetPasswordUser.email });
    }
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search users..."
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="p-8 text-center text-muted-foreground">
              Failed to load users.
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/30" />
              <h3 className="mt-4 text-lg font-semibold">No users yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                This tenant doesn't have any users.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <UserCircle className="h-8 w-8 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium">
                            {user.firstName || user.lastName
                              ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                              : user.email.split('@')[0]}
                          </p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>{getStatusBadge(user.status)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {user.lastLoginAt
                        ? formatDistanceToNow(new Date(user.lastLoginAt), { addSuffix: true })
                        : 'Never'}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                            <KeyRound className="mr-2 h-4 w-4" />
                            Reset Password
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination info */}
      {data?.pagination && data.pagination.total > 0 && (
        <p className="text-sm text-muted-foreground text-center">
          Showing {users.length} of {data.pagination.total} users
        </p>
      )}

      {/* Reset Password Confirmation Dialog */}
      <AlertDialog open={!!resetPasswordUser} onOpenChange={(open) => !open && setResetPasswordUser(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Password</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset the password for{' '}
              <strong>{resetPasswordUser?.email}</strong>?
              <br /><br />
              A new temporary password will be generated and sent to their email address.
              They will be required to change it on their next login.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmResetPassword}
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending ? 'Sending...' : 'Reset Password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function TenantsPage() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);

  // Fetch tenants
  const { data, isLoading, error } = useQuery({
    queryKey: ['tenants', searchQuery, statusFilter, planFilter],
    queryFn: async () => {
      const result = await tenantsApi.list({
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        plan: planFilter || undefined,
        limit: 50,
      });
      return result;
    },
    staleTime: 0,
    gcTime: 0,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateTenantInput) => tenantsApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      const adminUser = response.data?.adminUser;
      toast.success('Tenant created successfully', {
        description: adminUser
          ? `Admin user ${adminUser.email} has been created.`
          : `${formData.name} is now ready to use.`,
      });
      setIsCreating(false);
      setFormData(initialFormData);
      // Select the newly created tenant
      if (response.data) {
        setSelectedTenant(response.data);
      }
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      const message = errorData?.error || errorData?.message || error?.message || 'Failed to create tenant';
      toast.error('Failed to create tenant', { description: message });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantInput }) =>
      tenantsApi.update(id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant updated successfully');
      setIsEditing(false);
      // Update selected tenant with new data
      if (response.data) {
        setSelectedTenant(response.data);
      }
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      const message = errorData?.error || errorData?.message || error?.message || 'Failed to update tenant';
      toast.error('Failed to update tenant', { description: message });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tenantsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      const deletedName = selectedTenant?.name;
      setDeleteDialogOpen(false);
      setSelectedTenant(null);
      toast.success('Tenant deleted', {
        description: deletedName ? `${deletedName} has been permanently deleted.` : undefined,
      });
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      const message = errorData?.error || errorData?.message || error?.message || 'Failed to delete tenant';
      toast.error('Failed to delete tenant', { description: message });
    },
  });

  // Update form when selecting a tenant for editing
  useEffect(() => {
    if (selectedTenant && isEditing) {
      setFormData({
        ...initialFormData,
        name: selectedTenant.name,
        slug: selectedTenant.slug,
        plan: selectedTenant.plan,
        status: selectedTenant.status,
      });
    }
  }, [selectedTenant, isEditing]);

  const handleSelectTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsCreating(false);
    setIsEditing(false);
  };

  const handleCreateNew = () => {
    setSelectedTenant(null);
    setIsCreating(true);
    setIsEditing(false);
    setFormData(initialFormData);
    setShowPassword(false);
  };

  const handleEdit = () => {
    if (selectedTenant) {
      setFormData({
        ...initialFormData,
        name: selectedTenant.name,
        slug: selectedTenant.slug,
        plan: selectedTenant.plan,
        status: selectedTenant.status,
      });
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (isCreating) {
      createMutation.mutate({
        name: formData.name,
        slug: formData.slug,
        plan: formData.plan,
        status: formData.status,
        adminEmail: formData.adminEmail || undefined,
        adminPassword: formData.adminPassword || undefined,
        adminFirstName: formData.adminFirstName || undefined,
        adminLastName: formData.adminLastName || undefined,
        sendWelcomeEmail: formData.sendWelcomeEmail,
      });
    } else if (isEditing && selectedTenant) {
      updateMutation.mutate({
        id: selectedTenant.id,
        data: {
          name: formData.name,
          slug: formData.slug,
          plan: formData.plan,
          status: formData.status,
        },
      });
    }
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedTenant) {
      deleteMutation.mutate(selectedTenant.id);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const isEmailValid = (email: string) => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const canSubmit = () => {
    if (!formData.name) return false;
    if (isCreating && formData.adminEmail) {
      if (!isEmailValid(formData.adminEmail)) return false;
    }
    return true;
  };

  const getStatusBadge = (status: Tenant['status']) => {
    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </Badge>
        );
      case 'suspended':
        return (
          <Badge variant="destructive">
            <XCircle className="mr-1 h-3 w-3" />
            Suspended
          </Badge>
        );
      case 'trial':
        return (
          <Badge variant="secondary">
            <Clock className="mr-1 h-3 w-3" />
            Trial
          </Badge>
        );
    }
  };

  const getPlanBadge = (plan: Tenant['plan']) => {
    switch (plan) {
      case 'starter':
        return <Badge variant="outline">Starter</Badge>;
      case 'professional':
        return <Badge variant="secondary">Professional</Badge>;
      case 'enterprise':
        return (
          <Badge variant="default" className="bg-purple-500">
            Enterprise
          </Badge>
        );
    }
  };

  const tenants = Array.isArray(data?.data) ? data.data : [];

  return (
    <div className="h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tenants</h1>
          <p className="text-muted-foreground">
            Manage tenants on the platform
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Add Tenant
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4 mb-4">
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-xl font-bold">{data?.pagination?.total || 0}</p>
            </div>
            <Building2 className="h-8 w-8 text-muted-foreground/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-xl font-bold text-green-600">
                {tenants.filter((t) => t.status === 'active').length}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Trial</p>
              <p className="text-xl font-bold text-yellow-600">
                {tenants.filter((t) => t.status === 'trial').length}
              </p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500/30" />
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Enterprise</p>
              <p className="text-xl font-bold text-purple-600">
                {tenants.filter((t) => t.plan === 'enterprise').length}
              </p>
            </div>
            <Building2 className="h-8 w-8 text-purple-500/30" />
          </div>
        </Card>
      </div>

      {/* Main Content - Master-Detail Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-10 gap-4 h-[calc(100%-10rem)]">
        {/* Left Panel - Tenant List (40%) */}
        <Card className="lg:col-span-4 flex flex-col overflow-hidden">
          <CardHeader className="pb-3 flex-shrink-0">
            <CardTitle className="text-base">All Tenants</CardTitle>
            <div className="flex flex-col gap-2 pt-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <div className="flex gap-2">
                <Select
                  value={statusFilter || 'all'}
                  onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={planFilter || 'all'}
                  onValueChange={(v) => setPlanFilter(v === 'all' ? '' : v)}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full">
              {isLoading ? (
                <div className="space-y-2 p-4">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : error ? (
                <div className="py-8 text-center text-muted-foreground">
                  Failed to load tenants.
                </div>
              ) : tenants.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No tenants found.
                </div>
              ) : (
                <div className="divide-y">
                  {tenants.map((tenant) => (
                    <div
                      key={tenant.id}
                      onClick={() => handleSelectTenant(tenant)}
                      className={cn(
                        'p-3 cursor-pointer hover:bg-muted/50 transition-colors',
                        selectedTenant?.id === tenant.id && 'bg-muted'
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 flex-shrink-0">
                            <Building2 className="h-4 w-4 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{tenant.name}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {tenant.slug}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          {getStatusBadge(tenant.status)}
                          {getPlanBadge(tenant.plan)}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {tenant.user_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {tenant.document_count || 0}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(tenant.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Right Panel - Details/Edit (60%) */}
        <Card className="lg:col-span-6 flex flex-col overflow-hidden">
          {!selectedTenant && !isCreating ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <Building2 className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Select a Tenant</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Choose a tenant from the list to view details or create a new one.
                </p>
                <Button onClick={handleCreateNew}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create New Tenant
                </Button>
              </div>
            </div>
          ) : isCreating ? (
            // Create New Tenant Form
            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Create New Tenant</h2>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-6">
                  {/* Tenant Details */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      Tenant Details
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Tenant Name *</Label>
                        <Input
                          id="name"
                          placeholder="Acme Corporation"
                          value={formData.name}
                          onChange={(e) => {
                            const name = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              name,
                              slug: generateSlug(name),
                            }));
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="slug">Slug</Label>
                        <Input
                          id="slug"
                          placeholder="acme-corporation"
                          value={formData.slug}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, slug: e.target.value }))
                          }
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="plan">Plan</Label>
                        <Select
                          value={formData.plan}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              plan: v as 'starter' | 'professional' | 'enterprise',
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="starter">Starter</SelectItem>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="enterprise">Enterprise</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status">Status</Label>
                        <Select
                          value={formData.status}
                          onValueChange={(v) =>
                            setFormData((prev) => ({
                              ...prev,
                              status: v as 'active' | 'suspended' | 'trial',
                            }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Admin User Section */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                      <Users className="h-4 w-4" />
                      Admin User (Optional)
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="adminFirstName">First Name</Label>
                        <Input
                          id="adminFirstName"
                          placeholder="John"
                          value={formData.adminFirstName}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, adminFirstName: e.target.value }))
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="adminLastName">Last Name</Label>
                        <Input
                          id="adminLastName"
                          placeholder="Smith"
                          value={formData.adminLastName}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, adminLastName: e.target.value }))
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="adminEmail">Email Address</Label>
                      <Input
                        id="adminEmail"
                        type="email"
                        placeholder="john@acme.com"
                        value={formData.adminEmail}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, adminEmail: e.target.value }))
                        }
                        className={
                          formData.adminEmail && !isEmailValid(formData.adminEmail)
                            ? 'border-destructive'
                            : ''
                        }
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="adminPassword">Password (Optional)</Label>
                      <div className="relative">
                        <Input
                          id="adminPassword"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Leave empty to auto-generate"
                          value={formData.adminPassword}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, adminPassword: e.target.value }))
                          }
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sendWelcomeEmail"
                        checked={formData.sendWelcomeEmail}
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({ ...prev, sendWelcomeEmail: checked === true }))
                        }
                      />
                      <label htmlFor="sendWelcomeEmail" className="text-sm flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Send welcome email with login instructions
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!canSubmit() || createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Creating...' : 'Create Tenant'}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : selectedTenant && isEditing ? (
            // Edit Tenant Form
            <ScrollArea className="flex-1">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Edit Tenant</h2>
                  <Button variant="ghost" size="icon" onClick={handleCancelEdit}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-name">Tenant Name *</Label>
                      <Input
                        id="edit-name"
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, name: e.target.value }))
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-slug">Slug</Label>
                      <Input
                        id="edit-slug"
                        value={formData.slug}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, slug: e.target.value }))
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-plan">Plan</Label>
                      <Select
                        value={formData.plan}
                        onValueChange={(v) =>
                          setFormData((prev) => ({
                            ...prev,
                            plan: v as 'starter' | 'professional' | 'enterprise',
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="starter">Starter</SelectItem>
                          <SelectItem value="professional">Professional</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-status">Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(v) =>
                          setFormData((prev) => ({
                            ...prev,
                            status: v as 'active' | 'suspended' | 'trial',
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="trial">Trial</SelectItem>
                          <SelectItem value="suspended">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={!formData.name || updateMutation.isPending}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </ScrollArea>
          ) : selectedTenant ? (
            // View Tenant Details
            <Tabs defaultValue="overview" className="flex-1 flex flex-col">
              <div className="p-4 border-b flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold">{selectedTenant.name}</h2>
                      <p className="text-sm text-muted-foreground">{selectedTenant.slug}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={handleDelete}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  {getStatusBadge(selectedTenant.status)}
                  {getPlanBadge(selectedTenant.plan)}
                </div>
              </div>

              <TabsList className="mx-4 mt-2 w-fit">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="users">Users</TabsTrigger>
                <TabsTrigger value="usage">Usage & Stats</TabsTrigger>
                <TabsTrigger value="settings">Settings</TabsTrigger>
              </TabsList>

              <ScrollArea className="flex-1">
                <TabsContent value="overview" className="p-4 m-0">
                  {/* Quick Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Users</span>
                      </div>
                      <p className="text-xl font-bold mt-1">{selectedTenant.user_count || 0}</p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Documents</span>
                      </div>
                      <p className="text-xl font-bold mt-1">{selectedTenant.document_count || 0}</p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Departments</span>
                      </div>
                      <p className="text-xl font-bold mt-1">-</p>
                    </Card>
                    <Card className="p-3">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Conversations</span>
                      </div>
                      <p className="text-xl font-bold mt-1">-</p>
                    </Card>
                  </div>

                  {/* Details */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Tenant Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Tenant ID</p>
                          <p className="font-mono text-xs mt-1">{selectedTenant.id}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Slug</p>
                          <p className="font-mono mt-1">{selectedTenant.slug}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Plan</p>
                          <div className="mt-1">{getPlanBadge(selectedTenant.plan)}</div>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Status</p>
                          <div className="mt-1">{getStatusBadge(selectedTenant.status)}</div>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Created</p>
                          <p className="mt-1">
                            {new Date(selectedTenant.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Last Updated</p>
                          <p className="mt-1">
                            {new Date(selectedTenant.updated_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="users" className="p-4 m-0">
                  <TenantUsersTab tenantId={selectedTenant.id} />
                </TabsContent>

                <TabsContent value="usage" className="p-4 m-0">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        Usage Statistics
                      </CardTitle>
                      <CardDescription>
                        Resource usage and activity metrics
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-8 text-muted-foreground">
                        <Activity className="h-12 w-12 mx-auto mb-4 opacity-30" />
                        <p>Usage statistics will be available soon.</p>
                        <p className="text-sm">Track API calls, storage, and user activity.</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="settings" className="p-4 m-0">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Tenant Settings</CardTitle>
                      <CardDescription>
                        Configure tenant-specific settings
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center py-8 text-muted-foreground">
                        <Building2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                        <p>Advanced settings coming soon.</p>
                        <p className="text-sm">Configure limits, features, and integrations.</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            </Tabs>
          ) : null}
        </Card>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tenant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedTenant?.name}"? This will
              permanently delete all associated users, documents, and data. This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
